import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { supabase } from '../supabase';
import { useUserStore } from './user';
import ApiService from '../service/ApiService';
import { finishOnboardingPath } from '../composables/onboardingPath.mjs';
import { getDuesOnboardingProgress } from '../composables/duesOnboarding.mjs';
import {
    emptyAccountingProgress,
    emptyDuesProgress,
    goalSelectionUpdate,
    isAccountingProgressComplete,
    isDuesProgressComplete,
} from '../composables/onboardingProgress.mjs';

export const useOnboardingStore = defineStore('onboarding', {
    state: () => ({
        record: null,
        loaded: false,
        redirectToWizard: false,
        accountingProgress: emptyAccountingProgress(),
        duesProgress: emptyDuesProgress(),
    }),
    getters: {
        // Legacy completion exempts existing accounts from automatic onboarding.
        isGrandfathered: (state) => state.record?.status === 'completed',
        isAccountingComplete: (state) => isAccountingProgressComplete(state.accountingProgress),
        isDuesComplete: (state) => isDuesProgressComplete(state.duesProgress),
        isDuesReady: (state) => isDuesProgressComplete(state.duesProgress),
    },
    actions: {
        async ensureLoaded() {
            try {
                const userStore = useUserStore();
                if (!userStore.accountId) await userStore.getUserAccountId();
                const accountId = userStore.accountId;
                if (this.loaded && this.record?.account_id === accountId) return;
                this.$reset();
                const { data, error } = await supabase.from('account_onboarding')
                    .select('*').eq('account_id', accountId).maybeSingle();
                if (error) throw error;
                if (data) {
                    this.record = data;
                } else {
                    const fresh = await this.accountIsFresh(accountId);
                    const { error: insertError } = await supabase.from('account_onboarding').upsert({
                        account_id: accountId,
                        status: fresh ? 'in_progress' : 'completed',
                        completed_at: fresh ? null : new Date().toISOString(),
                    }, { onConflict: 'account_id', ignoreDuplicates: true });
                    if (insertError) throw insertError;
                    const { data: record, error: readError } = await supabase.from('account_onboarding')
                        .select('*').eq('account_id', accountId).single();
                    if (readError) throw readError;
                    this.record = record;
                    this.redirectToWizard = fresh && record.status === 'in_progress' && !record.dismissed;
                }
                this.loaded = true;
            } catch (error) {
                // A setup reminder must never block access to the app.
                console.error('Error loading onboarding:', error);
            }
        },
        async accountIsFresh(accountId) {
            const results = await Promise.all([
                supabase.from('members').select('member_id').eq('account_id', accountId).is('deleted_at', null).limit(1),
                supabase.from('dues_product').select('dues_product_id').eq('account_id', accountId).limit(1),
                supabase.from('invoices').select('invoice_id').eq('account_id', accountId).limit(1),
                supabase.from('financial_connections_accounts').select('financial_connections_account_id').eq('account_id', accountId).limit(1),
                supabase.from('transactions').select('transaction_id').eq('account_id', accountId).limit(1),
                // Default budget rows have zero amounts and are not user activity.
                supabase.from('budget_items').select('budget_item_id').eq('account_id', accountId).neq('amount', 0).limit(1),
            ]);
            for (const result of results) if (result.error) throw result.error;
            return results.every((result) => !result.data?.length);
        },
        async refreshAccountingProgress() {
            await this.ensureLoaded();
            if (!this.record) return;
            const [budgets, banks] = await Promise.all([
                supabase.from('budget_items').select('budget_item_id')
                    .eq('account_id', this.record.account_id).neq('amount', 0).limit(1),
                supabase.from('financial_connections_accounts').select('financial_connections_account_id')
                    .eq('account_id', this.record.account_id).limit(1),
            ]);
            if (budgets.error) throw budgets.error;
            if (banks.error) throw banks.error;
            this.accountingProgress = {
                categories: !!budgets.data.length,
                bank: !!banks.data.length,
                reviewed: !!this.record.transaction_reviewed_at,
            };
            if (this.isAccountingComplete) finishOnboardingPath('accounting');
        },
        async refreshDuesProgress() {
            await this.ensureLoaded();
            if (!this.record) return;
            const api = new ApiService();
            const userStore = useUserStore();
            const [members, products, subscriptions, invoices] = await Promise.all([
                api.getMembers(),
                api.getDuesProducts(),
                api.getSubscriptions(),
                api.getInvoices(),
                userStore.fetchConnectStatus(),
            ]);
            this.duesProgress = getDuesOnboardingProgress({
                members,
                products,
                subscriptions,
                invoices,
                payoutsReady: userStore.isConnectReady,
            });
            if (this.isDuesComplete) finishOnboardingPath('dues');
        },
        async refreshAllProgress() {
            await this.ensureLoaded();
            await Promise.all([
                this.refreshAccountingProgress(),
                this.refreshDuesProgress(),
            ]);
        },
        async update(fields) {
            if (!this.record) throw new Error('Setup could not be loaded. Please reload and try again.');
            const { error } = await supabase.from('account_onboarding').update(fields)
                .eq('account_id', this.record.account_id);
            if (error) throw error;
            this.record = { ...this.record, ...fields };
        },
        async chooseGoal(goal) {
            await this.update(goalSelectionUpdate(goal));
            posthog.capture('onboarding_goal_selected', { goal });
        },
        async dismiss() {
            await this.update({ dismissed: true });
            posthog.capture('onboarding_dismissed', { goal: this.record.goal });
        },
        async completeAccountingSetup() {
            await this.refreshAccountingProgress();
            if (!this.accountingProgress.categories || !this.accountingProgress.bank) return false;
            if (this.record.transaction_reviewed_at && this.record.accounting_completed_at) return true;
            const completedAt = new Date().toISOString();
            const fields = {};
            if (!this.record.transaction_reviewed_at) fields.transaction_reviewed_at = completedAt;
            if (!this.record.accounting_completed_at) fields.accounting_completed_at = completedAt;
            if (Object.keys(fields).length) await this.update(fields);
            this.accountingProgress.reviewed = true;
            finishOnboardingPath('accounting');
            posthog.capture('onboarding_report_reviewed', { goal: 'accounting' });
            posthog.capture('onboarding_completed', { goal: 'accounting', outcome: 'accounting' });
            posthog.capture('onboarding_value_reached', { goal: 'accounting', outcome: 'accounting' });
            return true;
        },
        async completeDuesSetup(outcome) {
            await this.refreshDuesProgress();
            if (!this.isDuesReady) return false;
            await this.markDuesCompleted(outcome);
            return true;
        },
        async markDuesCompleted(outcome) {
            if (!this.isDuesReady) return false;
            if (this.record.dues_completed_at) return true;
            const completedAt = new Date().toISOString();
            await this.update({ dues_completed_at: completedAt });
            posthog.capture('onboarding_completed', { goal: 'dues', outcome });
            if (outcome === 'recurring_billing' || outcome === 'one_off_invoice' || outcome === 'existing_billing') {
                posthog.capture('onboarding_value_reached', { goal: 'dues', outcome });
            }
            return true;
        },
    },
});
